<template>
  <div class="heading-1">Password Reset</div>
  <p class="text-3.4 font-400">
    {{
      sent
        ? "Please check your inbox to change your password."
        : "Reset your seller account password"
    }}
  </p>

  <div class="mt-8">
    <div v-if="sent" class="subheader my-8">
      A password reset link has been sent to
      <b>{{ credentials.email }}</b>
    </div>
    <template v-else>
      <v-dynamic-form
        v-model="credentials"
        v-model:valid="valid"
        :inputs="inputs"
        :readonly="loading"
      />
      <v-btn
        @click="send"
        color="primary"
        :loading="loading"
        :disabled="!valid"
        rounded="lg"
        height="38"
        block
        >Submit</v-btn
      >
    </template>
  </div>

  <div class="mt-8 text-left">
    <v-btn :to="{ name: 'login' }" variant="text" class="ml--2 normal-case">
      <span>Back to login</span>
      <v-icon size="20">chevron_right</v-icon>
    </v-btn>
  </div>
  <v-alert
    v-if="error"
    class="my-2"
    border="end"
    type="error"
    colored-border
    density="compact"
    variant="tonal"
  >
    Invalid email
  </v-alert>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useToast } from "vue-toastification";
import {
  AuthResponseStatus,
  type ForgotPasswordInput,
} from "~/layers/admin/modules/auth/runtime/types/api";
import { Rule } from "~/services/Rules";

export default defineComponent({
  setup() {
    definePageMeta({
      layout: "auth",
      auth: false,
    });

    // page title: "Forgot Password"

    const { links } = useAppConfig();
    const auth = useAuth();
    const toast = useToast();

    const credentials = ref<ForgotPasswordInput>({ email: "" });
    const loading = ref(false);
    const error = ref(false);
    const sent = ref(false);

    const send = () => {
      loading.value = true;
      error.value = false;

      if (credentials.value) {
        const input = toRaw(credentials.value);
        auth
          .forgotPassword(input)
          .then((response) => {
            if (response?.status !== AuthResponseStatus.EMAIL_SENT) {
              error.value = true;
            } else {
              sent.value = true;
            }
          })
          .catch(() => {
            toast.error("Failed to send password reset request");
            error.value = true;
          })
          .finally(() => {
            loading.value = false;
          });
      }
    };

    return {
      links,
      credentials,
      sent,
      loading,
      error,
      send,
    };
  },
  data: () => ({
    valid: false,
    inputs: defineFormInputs({
      email: {
        hideName: true,
        type: "text",
        rules: Rule.required(),
        props: {
          label: "Email",
          density: "compact",
          variant: "outlined",
          rounded: "lg",
        },
      },
    }),
  }),
});
</script>
